import classNames from 'classnames'
import { FunctionComponent, h, JSX } from 'preact'

import { MinusIcon, PlusIcon } from '../../icon/icon'

export interface NumberfieldProps {
    modifier?: string
    class?: string
    id: string
    name?: string
    label?: string
    labelClass?: string
    value: number
    onChange?: (newValue: number) => unknown
    min?: number
    max?: number
    isInvalid?: boolean
    isDisabled?: boolean
    error?: string
    description?: string
}

export const Numberfield: FunctionComponent<NumberfieldProps> = (props: NumberfieldProps) => {
    const canDecrement = typeof props.min !== 'number' || props.value > props.min
    const canIncrement = typeof props.max !== 'number' || props.value < props.max

    const renderButton = (
        onClick: (() => void) | undefined,
        modifier: string,
        icon: JSX.Element,
        text: string,
    ): JSX.Element => (
        <button
            type="button"
            class={classNames('numberfield__button', modifier)}
            onClick={onClick}
            tabIndex={-1}
            aria-hidden="true"
            disabled={props.isDisabled || !onClick}
        >
            {icon}
            <span class="numberfield__button-text">{text}</span>
        </button>
    )

    const renderDecrementButton = (): JSX.Element =>
        renderButton(
            canDecrement ? () => props.onChange?.(props.value - 1) : undefined,
            classNames('numberfield__button--decrease', canDecrement && 'is-clickable'),
            <MinusIcon class="numberfield__button-icon" />,
            'decrease',
        )

    const renderInput = (): JSX.Element => (
        <input
            class="numberfield__input"
            type="number"
            id={props.id}
            name={props.name}
            value={props.value}
            min={props.min}
            max={props.max}
            disabled={props.isDisabled}
            // TODO ...attributes?
            onInput={(event: JSX.TargetedEvent<HTMLInputElement>): void => {
                let newValue = parseInt(event.currentTarget.value, 10)

                if (isNaN(newValue)) {
                    // Restore value since there won't be a re-render
                    event.currentTarget.value = String(props.value)

                    return
                }

                if (typeof props.min === 'number' && newValue < props.min) {
                    newValue = props.min
                }

                if (typeof props.max === 'number' && newValue > props.max) {
                    newValue = props.max
                }

                // Sometimes the input value changes, but the clamped value is the
                // same as the previous one, so no re-render is triggered.
                // For example, when max is 10 and you change the input value from '10'
                // to '104', which gets changed back to 10.
                // In this case we need to manually reset the value (from '104' to '10').
                event.currentTarget.value = String(newValue)

                props.onChange?.(newValue)
            }}
        />
    )

    const renderIncrementButton = (): JSX.Element =>
        renderButton(
            canIncrement ? () => props.onChange?.(props.value + 1) : undefined,
            classNames('numberfield__button--increase', canIncrement && 'is-clickable'),
            <PlusIcon class="numberfield__button-icon" />,
            'increase',
        )

    const className = classNames('numberfield', props.modifier, props.class, {
        'is-invalid': props.isInvalid,
        'is-disabled': props.isDisabled,
    })

    return (
        <div class={className}>
            <div class="numberfield__inner">
                <label class={classNames('numberfield__label', props.labelClass)} for={props.id}>
                    {props.label}
                </label>
                <div class="numberfield__input-container">
                    {renderDecrementButton()}
                    {renderInput()}
                    {renderIncrementButton()}
                </div>
            </div>
            {props.error && props.isInvalid && <div class="numberfield__error">{props.error}</div>}
            {props.description && <div class="numberfield__description">{props.description}</div>}
        </div>
    )
}
